import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../constants/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

export interface NotificationModule {
  id: string;
  name: string;
  description?: string;
}

export interface NotificationSettings {
  moduleList: NotificationModule[];
  selectedModules: string[];
}

// Redundant ApiResponse removed

const NOTIFICATION_SETTINGS_URL = '/notification/getUserNotificationsList';
const NOTIFICATION_MODIFY_URL = '/notification/setUserNotifications';
const NOTIFICATION_SETTINGS_QUERY_KEY = 'notificationSettings';

const fetchNotificationSettings = async (): Promise<NotificationSettings> => {
  const { data } = await axiosInstance().post<{ data: NotificationSettings & { userNotificationsModuleList: NotificationSettings } }>(NOTIFICATION_SETTINGS_URL);
  // Based on the code, it seems it's either data.data or data.userNotificationsModuleList
  // The original code used data?.userNotificationsModuleList
  return (data as any)?.userNotificationsModuleList || (data as any)?.data;
};

const updateNotificationSettings = async (
  settings: any
): Promise<any> => {
  const { data } = await axiosInstance().post(
    NOTIFICATION_MODIFY_URL,
    settings
  );
  return data;
};

export const useNotificationSettings = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = [NOTIFICATION_SETTINGS_QUERY_KEY];
  const {
    data: settings,
    error,
    isLoading,
  } = useQuery<NotificationSettings, Error>({
    queryKey: queryKey,
    queryFn: fetchNotificationSettings,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: updateNotificationSettings,
    onSuccess: data => {
      // queryClient.invalidateQueries({queryKey: [NOTIFICATION_SETTINGS_QUERY_KEY]})
    },
    onMutate: async (newSettings: NotificationSettings) => {
      await queryClient.cancelQueries({ queryKey: queryKey });
      const previousSettings = queryClient.getQueryData<NotificationSettings>(queryKey);
      queryClient.setQueryData(queryKey, { ...previousSettings, selectedModules: newSettings?.moduleList });
      return { previousSettings };
    },
    onError: (err, newSettings, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(queryKey, context.previousSettings);
        toast.error(t('notifications.settings.updateFailedReverting', 'Failed to update settings. Reverting changes.'));
      } else {
        toast.error(t('notifications.settings.updateFailed', 'Failed to update settings.'));
      }
    },
  });

  const updateSettings = (newSettings: NotificationSettings) => {
    mutation.mutate(newSettings);
  };

  return {
    settings: settings ?? {},
    isLoading,
    error,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
    updateSettings,
  };
};
